import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { ArrowUpDown, Printer, FileDown } from 'lucide-react';
import DateRangeFilter from './DateRangeFilter';
import { exportCSV, inRange } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

export default function FuelVendorReport() {
  const [expenses, setExpenses] = useState([]);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('Total Spend');
  const [sortDir, setSortDir] = useState(-1);
  const company = useCompanyInfo();

  useEffect(() => {
    client.entities.Expense.filter({ category: 'Fuel' }, '-date', 1000).then(e => { setExpenses(e); setLoading(false); });
  }, []);

  const filtered = expenses.filter(e => inRange(e.date, start, end));

  const byVendor = {};
  filtered.forEach(e => {
    const key = e.vendor || 'Unspecified';
    if (!byVendor[key]) byVendor[key] = [];
    byVendor[key].push(e);
  });

  let rows = Object.entries(byVendor).map(([vendor, list]) => {
    const spend = list.reduce((s, e) => s + (e.amount || 0), 0);
    const gallons = list.reduce((s, e) => s + (parseFloat(e.gallons) || 0), 0);
    return {
      Vendor: vendor,
      'Fill-Ups': list.length,
      Gallons: gallons,
      'Total Spend': spend,
      'Avg Price/Gal': gallons > 0 ? spend / gallons : 0,
    };
  });

  rows.sort((a, b) => sortDir * ((b[sortKey] ?? 0) - (a[sortKey] ?? 0)));

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d * -1);
    else { setSortKey(key); setSortDir(-1); }
  };

  const csvRows = rows.map(r => ({ ...r, Gallons: r.Gallons.toFixed(3), 'Total Spend': r['Total Spend'].toFixed(2), 'Avg Price/Gal': r['Avg Price/Gal'].toFixed(3) }));

  const handlePrint = () => {
    const cols = ['Vendor', 'Fill-Ups', 'Gallons', 'Total Spend', 'Avg Price/Gal'];
    const dataRows = rows.map(r => [
      { value: r.Vendor, class: 'bold' }, r['Fill-Ups'], r.Gallons.toFixed(3),
      { value: `$${r['Total Spend'].toLocaleString('en-US', { minimumFractionDigits: 2 })}`, class: 'negative' },
      { value: `$${r['Avg Price/Gal'].toFixed(3)}`, class: 'gold' },
    ]);
    const grandSpend = rows.reduce((s, r) => s + r['Total Spend'], 0);
    const grandGallons = rows.reduce((s, r) => s + r.Gallons, 0);
    const totalsRow = ['TOTALS', rows.reduce((s, r) => s + r['Fill-Ups'], 0), grandGallons.toFixed(3), { value: `$${grandSpend.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, class: 'gold' }, ''];
    const cards = [
      { label: 'Vendors', value: rows.length, color: 'gold' },
      { label: 'Total Spend', value: `$${grandSpend.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: 'negative' },
      { label: 'Total Gallons', value: grandGallons.toLocaleString('en-US', { maximumFractionDigits: 1 }), color: '' },
    ];
    const html = generatePrintHTML('Fuel Vendor Report', `${start || 'All'} – ${end || 'All'}`, cols, dataRows, totalsRow, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter onChange={(s, e) => { setStart(s); setEnd(e); }} onExport={() => exportCSV(csvRows, 'fuel-vendor-report.csv')} exportDisabled={loading || rows.length === 0} />
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
        <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Vendor', 'Fill-Ups', 'Gallons', 'Total Spend', 'Avg Price/Gal'].map(h => (
                <th key={h} onClick={() => toggleSort(h)} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap cursor-pointer hover:text-foreground select-none">
                  <span className="flex items-center gap-1">{h} <ArrowUpDown className="w-3 h-3 opacity-50" /></span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map(r => (
              <tr key={r.Vendor} className="hover:bg-white/3">
                <td className="px-3 py-2.5 font-semibold text-foreground">{r.Vendor}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r['Fill-Ups']}</td>
                <td className="px-3 py-2.5 text-muted-foreground">{r.Gallons.toFixed(3)}</td>
                <td className="px-3 py-2.5 font-semibold text-red-400">${r['Total Spend'].toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                <td className="px-3 py-2.5 text-primary">${r['Avg Price/Gal'].toFixed(3)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No fuel expenses for this period.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
      <p className="text-xs text-muted-foreground">Fill-ups logged without a vendor are grouped under "Unspecified".</p>
    </div>
  );
}
